/**
 * Client-side CSV for design dashboard summary tables (matches on-screen columns).
 */

/** Strip newlines/tabs so Excel does not split one record into multiple rows. */
export const sanitizeCsvCell = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') {
        return fallback === null ? value : fallback;
    }
    return String(value)
        .replace(/[\r\n]+/g, ' ')
        .replace(/\t+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const buildStageBucketRow = (stageConfig, experienceCentreLabel, stages = {}) => {
    const row = { experienceCentre: experienceCentreLabel };
    stageConfig.forEach((stage) => {
        const bucket = stages[stage.key] || {};
        row[`${stage.key}_lte`] = Number(bucket.lte) || 0;
        row[`${stage.key}_gt`] = Number(bucket.gt) || 0;
    });
    row.grandTotal = stageConfig.reduce((sum, stage) => {
        const bucket = stages[stage.key] || {};
        return sum + (Number(bucket.lte) || 0) + (Number(bucket.gt) || 0);
    }, 0);
    return row;
};

/**
 * @param {object} options
 * @param {Array} options.stageConfig
 * @param {Array} options.rows — { experienceCentre, stages }
 * @param {object|null} options.totals — per-stage { lte, gt } keyed by stage.key
 * @param {(stage: object) => string} options.lteHeaderLabel
 * @param {(stage: object) => string} options.gtHeaderLabel
 */
export const buildTwoBucketStageSummaryCsv = ({
    stageConfig = [],
    rows = [],
    totals = null,
    lteHeaderLabel = (stage) => `<= ${stage.threshold} Days`,
    gtHeaderLabel = (stage) => `> ${stage.threshold} Days`,
}) => {
    const headers = [
        { label: 'Experience Centre', key: 'experienceCentre' },
        ...stageConfig.flatMap((stage) => [
            { label: `${stage.label} ${lteHeaderLabel(stage)}`, key: `${stage.key}_lte` },
            { label: `${stage.label} ${gtHeaderLabel(stage)}`, key: `${stage.key}_gt` },
        ]),
        { label: 'Grand Total', key: 'grandTotal' },
    ];

    const data = rows.map((row) =>
        buildStageBucketRow(stageConfig, row.experienceCentre, row.stages || {})
    );

    if (totals && typeof totals === 'object') {
        data.push(buildStageBucketRow(stageConfig, 'Total', totals));
    }

    return { headers, data };
};

export const buildMDaySummaryCsv = (displayBuckets = [], rows = []) => {
    const headers = [
        { label: 'Experience Centre', key: 'experienceCenter' },
        ...displayBuckets.map((b) => ({ label: b.label, key: b.key })),
        { label: 'Grand Total', key: 'grandTotal' },
    ];

    const data = rows.map((row) => {
        const out = { experienceCenter: row.experienceCenter };
        displayBuckets.forEach((b) => {
            out[b.key] = row[b.key]?.count ?? 0;
        });
        out.grandTotal = row.total?.count ?? 0;
        return out;
    });

    return { headers, data };
};
